import type { AskStreamEvent } from './types'
import { fanAiSportQuery } from './useFanAiConfig'
import { publicAuthHeaders } from '../../services/publicAccess'
import { messages } from '@/i18n'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'
import widgetsEn from '@/i18n/catalogs/site-core/widgets/en'

// Read the public API base URL the same way the rest of the app does.
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

/** English; streamAsk() sends the active language's (site-core widgets.fanAi.unavailable). */
export const FAN_AI_UNAVAILABLE_MESSAGE = widgetsEn.fanAi.unavailable

interface AskRequest {
  question: string
  // The fan's active sport; the backend answers with that sport's settings.
  sportId?: string | null
  conversationId: string
  history: Array<{ role: 'user' | 'assistant'; content: string }>
}

// Posts the question and returns an async iterator of SSE events.
// Cancellation is via AbortController.
export async function* streamAsk(
  req: AskRequest,
  signal: AbortSignal
): AsyncGenerator<AskStreamEvent> {
  const res = await fetch(`${API_BASE_URL}/public/fan-ai/ask${fanAiSportQuery(req.sportId)}`, {
    method: 'POST',
    // The private-site Bearer on a members-only league (registration step 33).
    headers: { ...publicAuthHeaders(), 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({
      question: req.question,
      conversation_id: req.conversationId,
      history: req.history,
    }),
    signal,
  })

  // The fan's language; messages() is safe here, site-core loads before first paint.
  const t = messages(siteCoreMessages).widgets.fanAi
  if (!res.ok) {
    let msg = t.error.generic
    let errorCode: string | undefined
    let lockReason: string | undefined
    try {
      const j = await res.json()
      errorCode = j?.error
      lockReason = typeof j?.reason === 'string' ? j.reason : undefined
      if (j?.error === 'rate_limited') msg = j.message || t.error.rateLimited
      else if (j?.error === 'budget_exhausted') msg = j.message || t.error.budgetExhausted
      else if (j?.error === 'question_too_long') msg = t.error.tooLong
    } catch { /* ignore */ }
    // 404 disabled / not_found and 403 (not entitled) mean the assistant is
    // switched off for this league, e.g. the plan changed while the sheet
    // was open (#328). Report it as "unavailable", not as a failure. The
    // shared public lockdown (switch model) answers 503 with a `reason` of
    // 'suspended' or 'not_live' when the league itself is locked; that is
    // "unavailable" too. (A 503 budget_exhausted has no reason and keeps its
    // own message.)
    if (
      (res.status === 503 && (lockReason === 'suspended' || lockReason === 'not_live')) ||
      res.status === 403 ||
      errorCode === 'disabled' ||
      errorCode === 'not_found' ||
      errorCode === 'feature_not_entitled' ||
      (res.status === 404 && !errorCode)
    ) {
      yield { type: 'error', unavailable: true, message: t.unavailable }
      return
    }
    yield { type: 'error', message: msg }
    return
  }

  if (!res.body) {
    yield { type: 'error', message: t.error.noBody }
    return
  }

  // SSE parser. Frames are separated by \n\n; each frame may contain
  // one or more lines like `event: foo` and `data: {...}`.
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''

  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      buf += decoder.decode(value, { stream: true })

      let nlnl: number
      while ((nlnl = buf.indexOf('\n\n')) !== -1) {
        const frame = buf.slice(0, nlnl)
        buf = buf.slice(nlnl + 2)
        const lines = frame.split('\n')
        let dataLine = ''
        for (const line of lines) {
          if (line.startsWith('data:')) {
            dataLine += line.slice(5).trimStart()
          }
          // We don't actually need the `event:` line — every frame's
          // payload object already includes a `type` field.
        }
        if (!dataLine) continue
        try {
          const ev = JSON.parse(dataLine) as AskStreamEvent
          yield ev
          if (ev.type === 'done' || ev.type === 'error') return
        } catch {
          // Malformed frame — skip rather than crash the whole stream.
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}
